<template>
    <a-modal
        :visible="true"
        width="45vw"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="sqm-wrap"
        @cancel="$emit('close')"
    >
        <div class="sqm-modal">
            <div class="sqm-head">
                <div class="sqm-head-top">
                    <div>
                        <h3>Quantidade por loja</h3>
                        <div class="sqm-sub">{{ product.descricaoProduto }} · {{ winner.nomeFornecedor }}</div>
                    </div>
                    <a-tooltip title="Fechar">
                        <a-button class="sqm-x" @click="$emit('close')">
                            <span class="material-icons-outlined">close</span>
                        </a-button>
                    </a-tooltip>
                </div>

                <div class="sqm-badges">
                    <span class="sqm-badge">EAN {{ ean || "—" }}</span>
                    <span class="sqm-badge">Cód. interno {{ internalCode || "—" }}</span>
                    <span class="sqm-badge">Embalagem {{ packagingLabel }}</span>
                    <span class="sqm-badge" v-if="product.flag_generico != null">
                        {{ product.flag_generico == 1 ? "Genérico" : "Não genérico" }}
                    </span>
                </div>

                <div class="sqm-price-box" :class="{ locked: hasGeneratedOrder || priceLocked }">
                    <span class="sqm-price-label">Preço unitário</span>
                    <a-input
                        ref="priceInput"
                        prefix="R$"
                        inputmode="numeric"
                        class="sqm-price-input"
                        v-model="priceInputValue"
                        :disabled="hasGeneratedOrder || priceLocked || is_saving_price"
                        @input="handlePriceInput"
                        @pressEnter="applyPrice"
                    />
                    <span v-if="hasGeneratedOrder" class="sqm-price-locked">Pedido já gerado — preço travado</span>
                    <a-button v-else-if="priceLocked" class="sqm-price-btn" @click="unlockPrice">
                        <span class="material-icons-outlined">lock_open</span>
                        Desbloquear
                    </a-button>
                    <a-button
                        v-else
                        class="sqm-price-btn"
                        :loading="is_saving_price"
                        :disabled="!isPriceDirty || is_saving_price"
                        @click="applyPrice"
                    >
                        Aplicar preço
                    </a-button>
                </div>
            </div>

            <div class="sqm-tools">
                <a-input v-model="search" class="sqm-search" placeholder="Buscar loja..." allow-clear>
                    <a-icon slot="prefix" type="search" />
                </a-input>
                <div class="sqm-bulk">
                    <a-input v-model="bulkValue" placeholder="0" @input="handleBulkInput" @pressEnter="applyToAll" />
                    <a-button class="sqm-bulk-btn" @click="applyToAll">Aplicar a todas</a-button>
                </div>
            </div>

            <cc-loader-full v-if="is_loading" />
            <div class="sqm-body" v-else>
                <table class="sqm-tbl">
                    <thead>
                        <tr>
                            <th class="left">Loja</th>
                            <th class="num">Requisitado</th>
                            <th class="num">Est.</th>
                            <th class="num center">Quantidade</th>
                            <th class="num">Qtd total</th>
                            <th class="num">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in filteredStores" :key="row.codLoja">
                            <td class="left">{{ row.descricaoLoja }}</td>
                            <td class="num">{{ row.quantidade || 0 }}</td>
                            <td class="num">{{ row.estoque != null ? $options.filters.stock(row.estoque) : "—" }}</td>
                            <td class="num center">
                                <input
                                    ref="qtyInput"
                                    type="number"
                                    min="0"
                                    class="sqm-in"
                                    v-model.number="row.quantidadeSugestao"
                                    :disabled="row.possuiPedido > 0"
                                    @wheel.prevent
                                    @keydown="handleQtyKeydown($event, row)"
                                />
                            </td>
                            <td class="num">{{ rowUnits(row) }}</td>
                            <td class="num">{{ rowTotal(row) | customCurrency }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="sqm-foot">
                <div class="sqm-tot">
                    Total <b>{{ totalUnits }} un</b> · <b>{{ totalValue | customCurrency }}</b>
                </div>
                <div class="sqm-btns">
                    <a-button class="sqm-btn-ghost" @click="$emit('close')">Cancelar</a-button>
                    <a-button
                        class="sqm-btn-pri"
                        :loading="is_saving"
                        :disabled="is_saving || is_saving_price"
                        @click="save"
                    >
                        Salvar
                    </a-button>
                </div>
            </div>
        </div>
    </a-modal>
</template>

<script>
import QuotationService from "@/services/QuotationService";
import OfferService from "@/services/v3/requests/offer.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import currencyPrecision from "@/mixins/currencyPrecision";

export default {
    name: "StoreQuantityModal",
    mixins: [currencyPrecision],
    props: {
        winner: { type: Object, required: true },
        product: { type: Object, required: true },
        quotationId: [String, Number],
        markWinnerOnSave: { type: Boolean, default: false },
    },
    data() {
        return {
            svc: new QuotationService(),
            offer_svc: new OfferService(),
            stores: [],
            initialQuantities: {},
            is_loading: true,
            is_saving: false,
            search: "",
            bulkValue: null,
            currentPrice: this.winner.valor || 0,
            initialPrice: this.winner.valor || 0,
            priceInputValue: this.formatPrice(Number(this.winner.valor) || 0),
            priceLocked: Number(this.winner.valor) > 0,
            is_saving_price: false,
        };
    },
    computed: {
        filteredStores() {
            if (!this.search) return this.stores;
            const term = this.search.toLowerCase();
            return this.stores.filter((s) => (s.descricaoLoja || "").toLowerCase().includes(term));
        },
        unitPrice() {
            return this.currentPrice;
        },
        hasGeneratedOrder() {
            return this.stores.some((s) => s.possuiPedido > 0);
        },
        isPriceDirty() {
            return this.parsePrice(this.priceInputValue) !== Number(this.initialPrice);
        },
        packaging() {
            return this.product.flag_generico == 1
                ? this.winner.embalagem
                : this.product.qtd_embalagem;
        },
        ean() {
            return this.product.ean || this.product.pro_ean || this.product.codEan || null;
        },
        internalCode() {
            return this.product.internal_code || this.product.pro_codInterno || this.product.codigoInterno || null;
        },
        packagingUnit() {
            const raw = this.product.embalagem || this.product.pro_embalagem || this.product.packing || "";
            const unit = String(raw).trim().split(/\s+/)[0];
            return unit && isNaN(unit) ? unit.toUpperCase() : null;
        },
        packagingLabel() {
            if (!this.packaging) return "—";
            return this.packagingUnit ? `${this.packagingUnit} ${this.packaging}` : `${this.packaging}`;
        },
        packagingMultiplier() {
            const value = Number(this.packaging);
            return value > 0 ? value : 1;
        },
        totalUnits() {
            return this.stores.reduce((sum, s) => sum + this.rowUnits(s), 0);
        },
        totalValue() {
            return this.totalUnits * this.unitPrice;
        },
    },
    created() {
        this.load();
    },
    methods: {
        formatPrice(value) {
            return Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        parsePrice(text) {
            return Number(String(text).replace(/\D/g, "")) / 100;
        },
        handlePriceInput(event) {
            const value = this.formatPrice(this.parsePrice(event.target.value));

            this.priceInputValue = value;
            event.target.value = value;
        },
        handleBulkInput(event) {
            const value = event.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "");

            this.bulkValue = value;
            event.target.value = value;
        },
        unlockPrice() {
            this.$swal
                .fire({
                    icon: "warning",
                    title: "Alterar preço negociado",
                    text: `Este produto já possui um preço digitado (R$ ${this.checkPrecisionValue(this.initialPrice)}). Ao desbloquear, você declara estar ciente de que está alterando um preço já negociado com o vendedor ${this.winner.nomeVendedor || this.winner.nomeFornecedor}.`,
                    showCancelButton: true,
                    confirmButtonText: "Estou ciente, desbloquear",
                    cancelButtonText: "Cancelar",
                })
                .then((result) => {
                    if (!result.isConfirmed) return;

                    this.priceLocked = false;
                    this.$nextTick(() => {
                        const el = this.$refs.priceInput;

                        if (el) {
                            el.focus();
                            el.select();
                        }
                    });
                });
        },
        async applyPrice() {
            if (this.hasGeneratedOrder || this.priceLocked || this.is_saving_price) return true;

            const price = this.parsePrice(this.priceInputValue);

            this.priceInputValue = this.formatPrice(price);

            if (price === Number(this.initialPrice)) return true;

            if (Number(this.initialPrice) > 0) {
                const result = await this.$swal.fire({
                    icon: "warning",
                    title: "Confirmar alteração de preço",
                    html: `Preço anterior: <b>${this.$options.filters.customCurrency(this.initialPrice)}</b><br>Novo preço: <b>${this.$options.filters.customCurrency(price)}</b>`,
                    showCancelButton: true,
                    confirmButtonText: "Confirmar",
                    cancelButtonText: "Cancelar",
                });

                if (!result.isConfirmed) return false;
            }

            this.is_saving_price = true;

            const succeeded = Number(this.initialPrice) > 0
                ? await this.commitPrice(price)
                : await this.commitNewOffer(price);

            this.is_saving_price = false;

            if (!succeeded) return false;

            this.priceLocked = true;
            return true;
        },
        async commitNewOffer(price) {
            const [first, ...rest] = this.stores;
            if (!first) return false;

            const offer = {
                request_id: this.quotationId,
                seller_id: this.winner.idVendedor,
                product_id: this.winner.pro_id,
                quantity: 0,
                price,
            };

            try {
                const response = await this.offer_svc.create({
                    ...offer,
                    store_ids: this.stores.map((s) => s.codLoja),
                    // LEGACY: back antigo exige store_id. Remover quando a kyojin com store_ids estiver em produção.
                    store_id: first.codLoja,
                });

                // LEGACY: back antigo ignora store_ids, grava só a 1ª loja e responde sem items; manda as demais uma por uma. Remover quando a kyojin com store_ids estiver em produção.
                if (!(response.data && response.data.items)) {
                    for (const store of rest) {
                        await this.offer_svc
                            .create({ ...offer, store_id: store.codLoja })
                            .catch((error) => ErrorHandlerService.handle(error, this.$store));
                    }
                }
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
                return false;
            }

            this.onPriceCommitted(price);
            return true;
        },
        commitPrice(price) {
            return this.svc
                .update_quotation_typing({
                    quotation_id: this.quotationId,
                    product_id: this.winner.pro_id,
                    seller_id: this.winner.idVendedor,
                    unit_price: price,
                })
                .then(() => {
                    this.onPriceCommitted(price);
                    return true;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                    return false;
                });
        },
        onPriceCommitted(price) {
            this.initialPrice = price;
            this.currentPrice = price;
            this.winner.valor = price;
            this.$emit("price-updated", price);
        },
        rowUnits(row) {
            return (Number(row.quantidadeSugestao) || 0) * this.packagingMultiplier;
        },
        rowTotal(row) {
            return this.rowUnits(row) * this.unitPrice;
        },
        load() {
            this.is_loading = true;
            this.svc
                .getLojasQuantidade(this.winner.pro_id, this.quotationId, this.winner.idVendedor)
                .then((response) => response.data)
                .then((data) => {
                    this.stores = data.produtos || [];
                    this.initialQuantities = {};
                    this.stores.forEach((s) => {
                        this.initialQuantities[s.codLoja] = s.quantidadeSugestao;
                    });
                    this.is_loading = false;
                })
                .catch((error) => {
                    this.is_loading = false;
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        handleQtyKeydown(event, row) {
            if (!["Enter", "ArrowDown", "ArrowUp"].includes(event.key)) return;

            event.preventDefault();

            const rows = this.filteredStores;
            const rowIndex = rows.findIndex((s) => s.codLoja === row.codLoja);

            if (rowIndex === -1) return;

            const step = event.key === "ArrowUp" ? -1 : 1;
            let nextRow = rowIndex + step;

            while (nextRow >= 0 && nextRow < rows.length && rows[nextRow].possuiPedido > 0) {
                nextRow += step;
            }

            if (nextRow < 0 || nextRow >= rows.length) return;

            this.$nextTick(() => {
                const inputs = this.$refs.qtyInput;
                const el = Array.isArray(inputs) ? inputs[nextRow] : null;

                if (el) {
                    el.focus();
                    el.select();
                }
            });
        },
        applyToAll() {
            if (this.bulkValue == null || this.bulkValue === "") return;
            const quantity = Number(this.bulkValue);
            this.filteredStores.forEach((s) => {
                if (!s.possuiPedido) this.$set(s, "quantidadeSugestao", quantity);
            });
        },
        async save() {
            if (this.isPriceDirty && !this.priceLocked && !this.hasGeneratedOrder) {
                this.is_saving = true;
                const priceSaved = await this.applyPrice();
                this.is_saving = false;

                if (!priceSaved) return;
            }

            const changed = this.stores.filter(
                (s) => Number(s.quantidadeSugestao || 0) !== Number(this.initialQuantities[s.codLoja] || 0)
            );
            if (!changed.length) {
                this.$emit("close");
                return;
            }
            this.is_saving = true;
            const markWinnersFirst = this.markWinnerOnSave
                ? Promise.all(
                      changed
                          .filter((s) => Number(s.quantidadeSugestao || 0) > 0)
                          .map((s) =>
                              this.svc.toggleQuotationWinner(
                                  this.quotationId,
                                  s.codLoja,
                                  this.winner.pro_id,
                                  this.winner.idVendedor,
                                  true
                              )
                          )
                  )
                : Promise.resolve();

            markWinnersFirst
                .then(() =>
                    Promise.all(
                        changed.map((s) =>
                            this.svc.sendQuantidade({
                                numerocotacao: this.quotationId,
                                quantidade: s.quantidadeSugestao || "0",
                                vendedor: this.winner.idVendedor,
                                produto: this.winner.pro_id,
                                loja: s.codLoja,
                            })
                        )
                    )
                )
                .then(() => {
                    this.is_saving = false;
                    this.$emit("saved");
                })
                .catch((error) => {
                    this.is_saving = false;
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.sqm-modal {
    --pri: var(--primary-color);
    --pri-bg: var(--primary-color-opacity, rgba(232, 96, 44, 0.08));

    max-height: 86vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.sqm-head {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.sqm-head-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
}
.sqm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.sqm-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.sqm-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    background: #f5f5f5;
    color: #595959;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
}
.sqm-price-box {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: var(--pri-bg);
    border: 1px solid var(--pri);
    border-radius: 8px;

    &.locked {
        background: #fafafa;
        border-color: #d9d9d9;
    }
}
.sqm-price-label {
    font-size: 13px;
    font-weight: 600;
    color: #262626;
}
.sqm-price-input {
    width: 130px;

    ::v-deep(.ant-input) {
        height: 34px;
        font-size: 15px;
        font-weight: 600;
        color: #262626;
    }
    ::v-deep(.ant-input[disabled]) {
        color: #bfbfbf;
    }
}
.sqm-price-btn.ant-btn,
.sqm-price-btn.ant-btn:hover,
.sqm-price-btn.ant-btn:focus,
.sqm-price-btn.ant-btn:active {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid var(--pri);
    background: #fff;
    color: var(--pri);
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    box-shadow: none;

    .material-icons-outlined {
        font-size: 16px;
    }
}
.sqm-price-btn.ant-btn[disabled],
.sqm-price-btn.ant-btn[disabled]:hover {
    border-color: #d9d9d9;
    background: #fff;
    color: #bfbfbf;
    cursor: not-allowed;
}
.sqm-price-locked {
    font-size: 12px;
    color: #8c8c8c;
    font-style: italic;
}
.sqm-x.ant-btn,
.sqm-x.ant-btn:focus,
.sqm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.sqm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.sqm-tools {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 0 20px 12px;
}
.sqm-search {
    flex: 1;

    ::v-deep(.ant-input) {
        height: 36px;
    }
}
.sqm-bulk {
    display: flex;
    align-items: center;
    gap: 6px;

    .ant-input {
        width: 64px;
        height: 36px;
        text-align: right;
    }
}
.sqm-bulk-btn.ant-btn,
.sqm-bulk-btn.ant-btn:hover,
.sqm-bulk-btn.ant-btn:focus,
.sqm-bulk-btn.ant-btn:active {
    height: 36px;
    padding: 0 14px;
    border: 1px solid var(--pri);
    background: var(--pri-bg);
    color: var(--pri);
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    box-shadow: none;
}
.sqm-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.sqm-tbl {
    width: 100%;
    border-collapse: collapse;

    thead th {
        position: sticky;
        top: 0;
        background: #fafafa;
        font-size: 12px;
        font-weight: 600;
        color: #8c8c8c;
        text-align: right;
        padding: 8px 10px;
        border-bottom: 1px solid #f0f0f0;

        &.left {
            text-align: left;
        }
        &.center {
            text-align: center;
        }
    }
    td {
        padding: 7px 10px;
        font-size: 14px;
        color: #434343;
        vertical-align: middle;
        border-bottom: 1px solid #f5f5f5;

        &.num {
            text-align: right;
        }
        &.left {
            text-align: left;
        }
        &.center {
            text-align: center;
        }
    }
}
.sqm-in {
    display: block;
    margin: 0 auto;
    width: 72px;
    height: 34px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    text-align: right;
    padding: 0 10px;
    font-size: 14px;
    color: #262626;

    &:focus {
        border-color: var(--pri);
        outline: none;
    }
    &:disabled {
        background: #f5f5f5;
        color: #bfbfbf;
    }
}
.sqm-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
.sqm-tot {
    font-size: 14px;
    color: #595959;

    b {
        font-size: 16px;
        color: #262626;
        margin-left: 6px;
    }
}
.sqm-btns {
    display: flex;
    gap: 10px;
}
.sqm-btn-ghost.ant-btn,
.sqm-btn-ghost.ant-btn:hover,
.sqm-btn-ghost.ant-btn:focus,
.sqm-btn-ghost.ant-btn:active {
    height: 40px;
    padding: 0 18px;
    border-radius: 10px;
    border: 1px solid #d9d9d9;
    background: #fff;
    color: #434343;
    font-weight: 500;
    font-size: 14px;
    box-shadow: none;
}
.sqm-btn-pri.ant-btn,
.sqm-btn-pri.ant-btn:hover,
.sqm-btn-pri.ant-btn:focus,
.sqm-btn-pri.ant-btn:active {
    height: 40px;
    padding: 0 18px;
    border-radius: 10px;
    border: none;
    background: var(--pri);
    color: #fff;
    font-weight: 600;
    font-size: 14px;
    box-shadow: none;
}
.sqm-btn-pri.ant-btn[disabled],
.sqm-btn-pri.ant-btn[disabled]:hover {
    background: var(--pri);
    color: #fff;
    opacity: 0.6;
    cursor: not-allowed;
}
</style>

<style lang="scss">
.sqm-wrap {
    .ant-modal {
        min-width: 600px;
        max-width: 92vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
